import { type ProjectActionItem } from '@consigli/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type GroupByOption, type SupportedItem } from '../components/action-item-types';
import {
  formatDateToLocale,
  getGroupKeyAndDisplay,
  getMeetingDatetimeFromGroup,
  parseEuropeanDateToTimestamp,
} from '../components/action-items-table-utils';

export interface GroupedItemsResult {
  /** Sorted array of [groupKey, group] entries */
  sortedGroups: Array<
    [string, { items: SupportedItem[]; displayName: string; subtitle: string | null }]
  >;
}

interface UseGroupedItemsOptions {
  items: SupportedItem[];
  groupBy: GroupByOption;
  /** Optional custom function to get group key */
  getGroupKey?: (item: SupportedItem, groupBy: GroupByOption) => string;
}

/**
 * Hook that groups and sorts action items based on the selected grouping option.
 * Encapsulates grouping and sorting logic for the ActionItemsTable.
 */
export const useGroupedItems = ({
  items,
  groupBy,
  getGroupKey: customGetGroupKey,
}: UseGroupedItemsOptions): GroupedItemsResult => {
  const { t, i18n } = useTranslation();

  // Translations for fallback values
  const translations = useMemo(
    () => ({
      unassigned: t('meeting-secretary.action-items.unassigned'),
      noTopic: t('meeting-secretary.action-items.no-topic'),
      noDeadline: t('meeting-secretary.action-items.no-deadline'),
      noMeeting: t('meeting-secretary.action-items.no-meeting'),
      statusLabel: (status: string) =>
        t(`meeting-secretary.action-items.status-${status.replace(/_/g, '-')}`),
      speakerPrefix: t('meeting-secretary.action-items.speaker-prefix'),
    }),
    [t],
  );

  // Group items and store display names and subtitles
  const groupedItems = useMemo(() => {
    const groups: Record<
      string,
      { items: SupportedItem[]; displayName: string; subtitle: string | null }
    > = {};

    items.forEach((item) => {
      let groupKey: string;
      let displayName: string;
      let subtitle: string | null;

      if (customGetGroupKey) {
        groupKey = customGetGroupKey(item, groupBy);
        displayName = groupKey;
        subtitle = null;
      } else {
        const result = getGroupKeyAndDisplay(item, groupBy, translations, i18n.language);
        groupKey = result.groupKey;
        displayName = result.displayName;
        subtitle = result.subtitle;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = { items: [], displayName, subtitle };
      }
      groups[groupKey].items.push(item);
    });

    // For meeting grouping, compute subtitle from the meeting date after items are grouped
    if (groupBy === 'meeting' && !customGetGroupKey) {
      Object.values(groups).forEach((group) => {
        if (!group.subtitle) {
          const datetime = getMeetingDatetimeFromGroup(group.items as ProjectActionItem[]);
          group.subtitle = datetime ? formatDateToLocale(datetime, i18n.language) : null;
        }
      });
    }

    return groups;
  }, [items, groupBy, customGetGroupKey, translations, i18n.language]);

  // Sort groups based on grouping type
  const sortedGroups = useMemo(
    () =>
      Object.entries(groupedItems).sort(([a, groupA], [b, groupB]) => {
        // For deadline grouping, sort: newest first, oldest last, no date at the end
        if (groupBy === 'deadline') {
          const timestampA = parseEuropeanDateToTimestamp(a);
          const timestampB = parseEuropeanDateToTimestamp(b);

          // Items with no date (timestamp 0) should sort last
          if (timestampA === 0 && timestampB === 0) return 0;
          if (timestampA === 0) return 1; // a (no date) goes after b
          if (timestampB === 0) return -1; // b (no date) goes after a

          // For valid dates, sort descending (newest first)
          return timestampB - timestampA;
        }

        // For meeting grouping, sort by meeting date descending (newest first)
        if (groupBy === 'meeting') {
          const itemA = groupA.items[0] as ProjectActionItem;
          const itemB = groupB.items[0] as ProjectActionItem;
          const dateA = itemA?.meetingScheduledStartTime;
          const dateB = itemB?.meetingScheduledStartTime;

          // Items with no date should sort last
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          // Sort descending (newest first)
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        }

        // For person grouping, sort by displayName (not groupKey) to avoid UUID prefix issues
        if (groupBy === 'person') {
          return groupA.displayName.localeCompare(groupB.displayName);
        }

        return a.localeCompare(b);
      }),
    [groupedItems, groupBy],
  );

  return { sortedGroups };
};
